<template>
  <div class="date-picker input">
    <div class="field-label">
      <label>{{label}}</label>
      <div v-if="helpText" class="field-help">
        <sprite name="help" />
        <tooltip class="field-helper-text" :content="helpText" />
      </div>
    </div>

    <select class="day" v-model="day">
      <option class="placeholder" value="">dd</option>

      <option
        class="value"
        v-for="d in availableDays"
        :key="d"
        :value="d"
        :selected="d === day">
        {{ d }}
      </option>
    </select>

    <span class="seperator">/</span>

    <select class="month" v-model="month">
      <option class="placeholder" value="">mm</option>

      <option
        class="value"
        v-for="m in availableMonths"
        :key="m"
        :value="m"
        :selected="m === month">
        {{ m }}
      </option>
    </select>

    <span class="seperator">/</span>

    <select class="year" v-model="year">
      <option class="placeholder" value="">yyyy</option>

      <option
        class="value"
        v-for="y in availableYears"
        :key="y"
        :value="y"
        :selected="y == year">
        {{ y }}
      </option>
    </select>
  </div>
</template>

<script>
import input from '@/mixins/input'
import Tooltip from '@/components/Tooltip'
import Sprite from '@/components/Sprite'

/**
 * pad a number with leading zeros to make it a required length
 *
 * @param  {number} number - number to pad
 * @param  {number} digits - length of ouput string
 * @returns {string} `number` padded with leading zeros to length `didgets`
 */
function padDidgets(number, digits) {
  return Array(Math.max(digits - String(number).length + 1, 0)).join(0) + number
}

const TOTAL_MONTHS = 12
const MAX_DAYS = 31

export default {
  name: 'DatePicker',

  mixins: [input],

  props: {
    yearFromOffset: {
      default: -10,
      type: Number
    },

    yearToOffset: {
      default: 0,
      type: Number
    },

    label: {
      type: String
    },

    helpText: {
      type: String
    }
  },

  components: {
    Tooltip,
    Sprite
  },

  data() {
    return {
      currentYear: new Date().getFullYear()
    }
  },

  computed: {
    hasValue() {
      return this.day && this.month && this.year
    },

    currentValue: {
      get() {
        if (!this.resource) {
          return ''
        }
        return this.resource.split('-')
      },

      set(newValue) {
        this.$emit('input', newValue.join('-'))
      }
    },

    day: {
      get() {
        return this.currentValue[2]
      },

      set(newDay) {
        let value = this.currentValue
        this.currentValue = [value[0], value[1], newDay]
      }
    },

    month: {
      get() {
        return this.currentValue[1]
      },

      set(newMonth) {
        let value = this.currentValue
        this.currentValue = [value[0], newMonth, value[2]]
      }
    },

    year: {
      get() {
        return this.currentValue[0]
      },

      set(newYear) {
        let value = this.currentValue
        this.currentValue = [newYear, value[1], value[2]]
      }
    },

    daysInMonth() {
      if (this.year && this.month) {
        return new Date(this.year, this.month, 0).getDate()
      } else {
        return MAX_DAYS
      }
    },

    availableDays() {
      return new Array(this.daysInMonth)
        .fill()
        .map((_d, i) => padDidgets(i + 1, 2))
        .reverse()
    },

    availableMonths() {
      return new Array(TOTAL_MONTHS)
        .fill()
        .map((_d, i) => padDidgets(i + 1, 2))
        .reverse()
    },

    yearFrom() {
      return this.currentYear + this.yearFromOffset + 1
    },

    yearTo() {
      return this.currentYear + this.yearToOffset + 1
    },

    availableYears() {
      let from = this.yearFrom
      let to = this.yearTo

      return new Array(to - from)
        .fill()
        .map((_d, i) => '' + (i + from))
        .reverse()
    }
  }
}
</script>

<style lang="sass" scoped>
.date-picker
  margin-bottom: 30px

  .seperator
    margin: 0 3px
</style>
